import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";

// Import components
import NotificationTable from "./components/NotificationTable";
import NotificationModal from "./components/NotificationModal";
import BulkActionsBar from "./components/BulkActionsBar";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
 
  getNotificationsInfoService,
} from "redux/services/general_service";
import { toast } from "react-toastify";

const NotificationsManagment = () => {
  const { t } = useTranslation();

  const { generalNotifications } = useSelector((state) => state.general);

  const dispatch = useDispatch();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [signals, setSignals] = useState([]);
  const [filteredSignals, setFilteredSignals] = useState([]);
  const [selectedSignals, setSelectedSignals] = useState([]);
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [editingSignal, setEditingSignal] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Mock signal data
  const mockSignals = [
    {
      id: 1,
      name: "Traffic Signal Update",
      description: "Update traffic signals in downtown area",
      type: "traffic",
      status: "active",
      budget: 5000,
      spent: 3200,
      dailyBudget: 200,
      startDate: "2024-06-01",
      endDate: "2024-08-31",
      impressions: 125000,
      clicks: 3750,
      ctr: 3.0,
      roi: 245,
      mediaUrl:
        "https://images.pexels.com/photos/1488463/pexels-photo-1488463.jpeg",
      targetAudience: ["drivers", "pedestrians"],
      locations: ["us", "ca"],
      objectives: "safety",
      bidStrategy: "auto",
      keywords: "traffic, signal, update, safety",
    },
    {
      id: 2,
      name: "Railway Signal Maintenance",
      description: "Maintain railway signals for efficient operations",
      type: "railway",
      status: "paused",
      budget: 3000,
      spent: 1800,
      dailyBudget: 150,
      startDate: "2024-07-15",
      endDate: "2024-09-15",
      impressions: 89000,
      clicks: 2670,
      ctr: 3.2,
      roi: 180,
      mediaUrl:
        "https://images.pexels.com/photos/159844/cellular-education-classroom-159844.jpeg",
      targetAudience: ["operators", "engineers"],
      locations: ["us"],
      objectives: "efficiency",
      bidStrategy: "manual",
      keywords: "railway, signal, maintenance, operations",
    },
    {
      id: 3,
      name: "Airport Signal Systems",
      description: "Implement new signal systems at airport",
      type: "airport",
      status: "scheduled",
      budget: 8000,
      spent: 0,
      dailyBudget: 300,
      startDate: "2024-11-01",
      endDate: "2024-12-25",
      impressions: 0,
      clicks: 0,
      ctr: 0,
      roi: 0,
      mediaUrl:
        "https://images.pexels.com/photos/1303081/pexels-photo-1303081.jpeg",
      targetAudience: ["pilots", "air_traffic_controllers"],
      locations: ["us", "ca", "uk"],
      objectives: "awareness",
      bidStrategy: "target_roas",
      keywords: "airport, signal, systems, aviation",
    },
    {
      id: 4,
      name: "Smart City Signals",
      description: "Deploy smart signals for urban management",
      type: "smart_city",
      status: "active",
      budget: 4500,
      spent: 2100,
      dailyBudget: 180,
      startDate: "2024-08-01",
      endDate: "2024-10-31",
      impressions: 95000,
      clicks: 4750,
      ctr: 5.0,
      roi: 320,
      mediaUrl:
        "https://images.pexels.com/photos/267350/pexels-photo-267350.jpeg",
      targetAudience: ["city_managers", "residents"],
      locations: ["us", "uk", "au"],
      objectives: "conversions",
      bidStrategy: "target_cpa",
      keywords: "smart city, signals, urban, management",
    },
    {
      id: 5,
      name: "Emergency Signal Alerts",
      description: "Enhance emergency signal alerts system",
      type: "emergency",
      status: "completed",
      budget: 2500,
      spent: 2500,
      dailyBudget: 100,
      startDate: "2024-05-01",
      endDate: "2024-05-31",
      impressions: 180000,
      clicks: 5400,
      ctr: 3.0,
      roi: 150,
      mediaUrl:
        "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg",
      targetAudience: ["emergency_services", "public"],
      locations: ["us", "ca", "uk", "au"],
      objectives: "awareness",
      bidStrategy: "auto",
      keywords: "emergency, signal, alerts, safety",
    },
    {
      id: 6,
      name: "Communication Signal Boost",
      description: "Boost communication signals in remote areas",
      type: "communication",
      status: "draft",
      budget: 1500,
      spent: 0,
      dailyBudget: 75,
      startDate: "2024-09-01",
      endDate: "2024-09-30",
      impressions: 0,
      clicks: 0,
      ctr: 0,
      roi: 0,
      mediaUrl:
        "https://images.pexels.com/photos/1591062/pexels-photo-1591062.jpeg",
      targetAudience: ["telecom_providers", "residents"],
      locations: ["us"],
      objectives: "coverage",
      bidStrategy: "manual",
      keywords: "communication, signal, boost, remote",
    },
  ];

  useEffect(() => {
    dispatch(getNotificationsInfoService({ page: 1 }));
    setSignals(generalNotifications);
    setFilteredSignals(generalNotifications);
  }, []);

  // Filter signals based on search and filters
  const applyFilters = (filters) => {
    let filtered = signals;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered?.filter(
        (signal) =>
          signal?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
          signal?.description
            ?.toLowerCase()
            ?.includes(searchTerm?.toLowerCase())
      );
    }

    // Apply status filter
    if (filters?.status) {
      filtered = filtered?.filter(
        (signal) => signal?.status === filters?.status
      );
    }

    // Apply signal type filter
    if (filters?.signalType) {
      filtered = filtered?.filter(
        (signal) => signal?.type === filters?.signalType
      );
    }

    // Apply budget range filter
    if (filters?.budgetRange?.min) {
      filtered = filtered?.filter(
        (signal) => signal?.budget >= parseFloat(filters?.budgetRange?.min)
      );
    }
    if (filters?.budgetRange?.max) {
      filtered = filtered?.filter(
        (signal) => signal?.budget <= parseFloat(filters?.budgetRange?.max)
      );
    }

    // Apply date range filter
    if (filters?.dateRange?.start) {
      filtered = filtered?.filter(
        (signal) =>
          new Date(signal.startDate) >= new Date(filters.dateRange.start)
      );
    }
    if (filters?.dateRange?.end) {
      filtered = filtered?.filter(
        (signal) =>
          new Date(signal.endDate) <= new Date(filters.dateRange.end)
      );
    }

    // Apply target audience filter
    if (filters?.targetAudience?.length > 0) {
      filtered = filtered?.filter((signal) =>
        filters?.targetAudience?.some((audience) =>
          signal?.targetAudience?.includes(audience)
        )
      );
    }

    // Apply search term filter from filters
    if (filters?.searchTerm) {
      filtered = filtered?.filter(
        (signal) =>
          signal?.name
            ?.toLowerCase()
            ?.includes(filters?.searchTerm?.toLowerCase()) ||
          signal?.description
            ?.toLowerCase()
            ?.includes(filters?.searchTerm?.toLowerCase())
      );
    }

    setFilteredSignals(filtered);
  };

  // Sort signals
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig?.key === key && sortConfig?.direction === "asc") {
      direction = "desc";
    }

    const sortedSignals = [...filteredSignals]?.sort((a, b) => {
      if (a?.[key] < b?.[key]) return direction === "asc" ? -1 : 1;
      if (a?.[key] > b?.[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });

    setFilteredSignals(sortedSignals);
    setSortConfig({ key, direction });
  };

  // Handle signal actions
  const handleEditSignal = (signal) => {
    setEditingSignal(signal);
    setShowSignalModal(true);
  };

  const handlePauseResumeSignal = (signal) => {
    const newStatus = signal?.status === "active" ? "paused" : "active";
    const updatedSignals = signals?.map((s) =>
      s?.id === signal?.id ? { ...s, status: newStatus } : s
    );
    setSignals(updatedSignals);
    applyFilters({});
  };
  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const handleDuplicateSignal = (signal) => {
    const duplicatedSignal = {
      ...signal,
      id: Date.now(),
      name: `${signal?.name} (Copy)`,
      status: "draft",
      spent: 0,
      impressions: 0,
      clicks: 0,
      ctr: 0,
      roi: 0,
    };
    const updatedSignals = [...signals, duplicatedSignal];
    setSignals(updatedSignals);
    applyFilters({});
  };

 /* const handleDeleteSignal = (signal) => {
    if (window.confirm(`Are you sure you want to delete "${signal?.id}"?`)) {
      dispatch(deleteSignalService({ id: signal?.id })).then(
        (response) => {
          if (
            response?.payload?.status == 200 ||
            response?.payload?.status == 204
          ) {
            toast("Deleted Success");
            dispatch(getGener({ page: 1 }));
          }
        }
      );
    }
  };*/

  const handleSaveSignal = (signalData) => {
    if (editingSignal) {
      // Update existing signal
      const updatedSignals = signals?.map((s) =>
        s?.id === editingSignal?.id ? { ...s, ...signalData } : s
      );
      setSignals(updatedSignals);
    } else {
      // Create new signal
      const newSignal = {
        ...signalData,
        id: Date.now(),
        spent: 0,
        impressions: 0,
        clicks: 0,
        ctr: 0,
        roi: 0,
        status: "draft",
        mediaUrl:
          signalData?.mediaFiles?.[0]?.url ||
          "https://images.pexels.com/photos/267350/pexels-photo-267350.jpeg",
      };
      setSignals([...signals, newSignal]);
    }
    setEditingSignal(null);
    applyFilters({});
  };



  // Apply search filter when search term changes
  useEffect(() => {
    applyFilters({});
    console.log(generalNotifications)
  }, [searchTerm, signals]);

  return (
    <>
      <Helmet>
        <title>Signal Management - Business Manager Pro</title>
        <meta
          name="description"
          content="Manage and optimize your signal systems with comprehensive analytics and performance tracking."
        />
      </Helmet>

      {/* Navigation Header
      <NavigationHeader
        user={user}
        onMenuToggle={handleSidebarToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

     
      <MainNavigation
        isCollapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      /> */}

      <div className="p-6">
        {/* Breadcrumb Navigation
            <BreadcrumbNavigation /> */}

        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t("notifications")}
            </h1>
          </div>

          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
      
            <Button
              onClick={() => {
                setEditingSignal(null);
                setShowSignalModal(true);
              }}
              iconName="Plus"
              iconPosition="left"
            >
          إرسال إشعار
          
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            <NotificationTable
              notifications={generalNotifications}
              selectedSignals={selectedSignals}
              onSelectionChange={setSelectedSignals}
              onEdit={handleEditSignal}
              onPauseResume={handlePauseResumeSignal}
              onDuplicate={handleDuplicateSignal}
            //  onDelete={handleDeleteSignal}
              sortConfig={sortConfig}
              onSort={handleSort}
            />

    
          </div>
        </div>
      </div>

      {/* Signal Modal */}
      <NotificationModal
        isOpen={showSignalModal}
        onClose={() => {
          setShowSignalModal(false);
          setEditingSignal(null);
        }}
        signal={editingSignal}
        onSave={handleSaveSignal}
      />
    </>
  );
};

export default NotificationsManagment;